import React, { useContext, useState, useMemo } from "react";
import useSWR from "swr";
import TableTotalNCost from "./TableTotalNCost";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import { useNavigationEvent } from "navigation-react";
import LoaderAnimation from "./LoaderAnimationLarge";
import SecureLS from "secure-ls";
import { AppContext } from "./AppContext";
import {
  faCancel,
  faCheckCircle,
  faClose,
  faEye,
  faMoneyBillTransfer,
  faCalculator,
  faCoins,
  faFileInvoice,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AddCTScanRequestRegister from "./AddCTScanRequestRegister";
import UpdateCTScanRequestPayout from "./UpdateCTScanRequestPayout";
import MessageModal from "./MessageModal";
const ls = new SecureLS({ encodingType: "aes" });
const usercsrf_token = ls.get("usercsrf_token");
const userid = ls.get("currentuserid");

const fetcher = (url) =>
  fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": usercsrf_token,
    },
  })
    .then((res) => res.json())
    .then((data) => (Array.isArray(data) ? data : [data]));

function parseDate(dateStr) {
  if (!dateStr) return null;

  const isoDate = new Date(dateStr);
  if (!isNaN(isoDate.getTime())) return isoDate;

  const parts = dateStr.split("/");
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    const parsedDate = new Date(year, month, day);
    if (!isNaN(parsedDate.getTime())) return parsedDate;
  }

  return null;
}

const CtScanReportRegister = () => {
  const { stateNavigator } = useNavigationEvent();
  const { setSelectedData } = useContext(AppContext);
  const [editRequestData, setEditRequestData] = useState("");
  const [modalAddRequest, setModalAddRequest] = useState(false);
  const [modalEditRequest, setModalEditRequest] = useState(false);
  const [isMessageModalIsNotOk, setIsMessageModalIsNotOk] = useState(false);
  const [isMessageModalError, setIsMessageModalError] = useState(false);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);

  // Filter states - Text fields
  const [patientIdFilter, setPatientIdFilter] = useState("");
  const [patientNameFilter, setPatientNameFilter] = useState("");
  const [investigationFilter, setInvestigationFilter] = useState(""); // Changed to text field
  const [investigationCostFilter, setInvestigationCostFilter] = useState("");
  const [requestFeeFilter, setRequestFeeFilter] = useState("");
  const [requestPercentageFilter, setRequestPercentageFilter] = useState("");

  // Filter states - Select fields
  const [reportStatusFilter, setReportStatusFilter] = useState("");
  const [requestedByFilter, setRequestedByFilter] = useState("");
  const [payoutStatusFilter, setPayoutStatusFilter] = useState("");

  // Filter states - Date fields
  const [requestStartDate, setRequestStartDate] = useState("");
  const [requestEndDate, setRequestEndDate] = useState("");
  const [reportedStartDate, setReportedStartDate] = useState("");
  const [reportedEndDate, setReportedEndDate] = useState("");

  // eslint-disable-next-line no-unused-vars
  const [modalMessages, setModalMessages] = useState("");

  const toggleModalAddRequest = () => setModalAddRequest(!modalAddRequest);
  const toggleModalEditRequest = () => setModalEditRequest(!modalEditRequest);
  const toggleModalSuccessMsg = () => setModalSuccessMsg(!modalSuccessMsg);
  const toggleModalError = () => setIsMessageModalError(!isMessageModalError);
  const toggleModalIsNotOk = () =>
    setIsMessageModalIsNotOk(!isMessageModalIsNotOk);

  const {
    data: ctrequestsData,
    error,
    isLoading,
  } = useSWR(
    userid ? `${process.env.REACT_APP_CT_SCAN_REPORT_URL}` : null,
    fetcher,
    {
      refreshInterval: 2000,
      revalidateOnFocus: true,
      refreshWhenHidden: true,
    },
  );

  console.log(
    "CT SCAN REQUEST DATA FROM CT SCAN REPORT REGISTER:",
    ctrequestsData,
  );

  // Calculate statistics - Removed averages
  const statistics = useMemo(() => {
    if (!Array.isArray(ctrequestsData) || ctrequestsData.length === 0) {
      return {
        totalInvestigationCost: 0,
        totalRequestFee: 0,
        totalRecords: 0,
      };
    }

    const costs = ctrequestsData.map(
      (item) => Number(item.field_investigation_price) || 0,
    );
    const fees = ctrequestsData.map(
      (item) => Number(item.field_requester_payment_commissi) || 0,
    );

    const totalInvestigationCost = costs.reduce((sum, cost) => sum + cost, 0);
    const totalRequestFee = fees.reduce((sum, fee) => sum + fee, 0);

    return {
      totalInvestigationCost,
      totalRequestFee,
      totalRecords: ctrequestsData.length,
    };
  }, [ctrequestsData]);

  // Get unique values for select filters (only for dropdowns)
  const uniqueValues = useMemo(() => {
    if (!ctrequestsData)
      return {
        reportStatuses: [],
        requesters: [],
        payoutStatuses: [],
      };

    const reportStatuses = [
      ...new Set(
        ctrequestsData
          .map((item) => item.field_ct_scan_report_status)
          .filter(Boolean),
      ),
    ];
    const requesters = [
      ...new Set(
        ctrequestsData
          .map((item) =>
            `${item.requester_first_name} ${item.requester_last_name}`.trim(),
          )
          .filter(Boolean),
      ),
    ];
    const payoutStatuses = [
      ...new Set(
        ctrequestsData.map((item) => item.field_payout_status).filter(Boolean),
      ),
    ];

    return { reportStatuses, requesters, payoutStatuses };
  }, [ctrequestsData]);

  // Filter the data based on all filter criteria
  const filteredData = useMemo(() => {
    if (!Array.isArray(ctrequestsData)) return [];

    return ctrequestsData.filter((request) => {
      // Text field filters (case insensitive partial match)
      const matchesPatientId =
        !patientIdFilter ||
        request.field_patient_id
          ?.toLowerCase()
          .includes(patientIdFilter.toLowerCase());

      const matchesPatientName =
        !patientNameFilter ||
        request.patient_fullname
          ?.toLowerCase()
          .includes(patientNameFilter.toLowerCase());

      const matchesInvestigation =
        !investigationFilter ||
        request.investigation
          ?.toLowerCase()
          .includes(investigationFilter.toLowerCase());

      const matchesInvestigationCost =
        !investigationCostFilter ||
        request.field_investigation_price
          ?.toLowerCase()
          .includes(investigationCostFilter.toLowerCase());

      const matchesRequestFee =
        !requestFeeFilter ||
        request.field_requester_payment_commissi
          ?.toLowerCase()
          .includes(requestFeeFilter.toLowerCase());

      const matchesRequestPercentage =
        !requestPercentageFilter ||
        request.field_requester_payment_com_perc
          ?.toLowerCase()
          .includes(requestPercentageFilter.toLowerCase());

      // Select field filters (exact match)
      const matchesReportStatus =
        !reportStatusFilter ||
        request.field_ct_scan_report_status === reportStatusFilter;

      const matchesRequestedBy =
        !requestedByFilter ||
        `${request.requester_first_name} ${request.requester_last_name}`.trim() ===
          requestedByFilter;

      const matchesPayoutStatus =
        !payoutStatusFilter ||
        request.field_payout_status === payoutStatusFilter;

      // Date filters
      const requestDate = parseDate(request.request_date);
      const reqStart = requestStartDate ? new Date(requestStartDate) : null;
      const reqEnd = requestEndDate ? new Date(requestEndDate) : null;

      const reportedDate = parseDate(request.report_date);
      const repStart = reportedStartDate ? new Date(reportedStartDate) : null;
      const repEnd = reportedEndDate ? new Date(reportedEndDate) : null;

      // Normalize dates for comparison
      if (reqStart) reqStart.setHours(0, 0, 0, 0);
      if (reqEnd) reqEnd.setHours(23, 59, 59, 999);
      if (requestDate) requestDate.setHours(0, 0, 0, 0);

      if (repStart) repStart.setHours(0, 0, 0, 0);
      if (repEnd) repEnd.setHours(23, 59, 59, 999);
      if (reportedDate) reportedDate.setHours(0, 0, 0, 0);

      const isInRequestDateRange =
        (!reqStart || (requestDate && requestDate >= reqStart)) &&
        (!reqEnd || (requestDate && requestDate <= reqEnd));

      const isInReportedDateRange =
        (!repStart || (reportedDate && reportedDate >= repStart)) &&
        (!repEnd || (reportedDate && reportedDate <= repEnd));

      return (
        matchesPatientId &&
        matchesPatientName &&
        matchesInvestigation &&
        matchesInvestigationCost &&
        matchesRequestFee &&
        matchesRequestPercentage &&
        matchesReportStatus &&
        matchesRequestedBy &&
        matchesPayoutStatus &&
        isInRequestDateRange &&
        isInReportedDateRange
      );
    });
  }, [
    ctrequestsData,
    patientIdFilter,
    patientNameFilter,
    investigationFilter,
    investigationCostFilter,
    requestFeeFilter,
    requestPercentageFilter,
    reportStatusFilter,
    requestedByFilter,
    payoutStatusFilter,
    requestStartDate,
    requestEndDate,
    reportedStartDate,
    reportedEndDate,
  ]);

  // Style for Report Status
  const getReportStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return {
          color: "#ffffff",
          backgroundColor: "#dc3545",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
          display: "inline-block",
        };
      case "assigned":
        return {
          color: "#ffffff",
          backgroundColor: "#fd7e14",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
          display: "inline-block",
        };
      case "completed":
        return {
          color: "#ffffff",
          backgroundColor: "#28a745",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
          display: "inline-block",
        };
      case "in progress":
        return {
          color: "#ffffff",
          backgroundColor: "#17a2b8",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
          display: "inline-block",
        };
      case "cancelled":
        return {
          color: "#ffffff",
          backgroundColor: "#6c757d",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
          display: "inline-block",
        };
      default:
        return {
          color: "#6c757d",
          backgroundColor: "#f8f9fa",
          padding: "4px 8px",
          borderRadius: "4px",
          border: "1px solid #dee2e6",
          display: "inline-block",
        };
    }
  };

  // Style for Payout Status
  const getPayoutStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return {
          color: "#ffffff",
          backgroundColor: "#28a745",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
          display: "inline-block",
        };
      case "processing":
        return {
          color: "#ffffff",
          backgroundColor: "#fd7e14",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
          display: "inline-block",
        };
      case "disputed":
        return {
          color: "#ffffff",
          backgroundColor: "#dc3545",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
          display: "inline-block",
        };
      case "pending":
        return {
          color: "#ffffff",
          backgroundColor: "#6c757d",
          padding: "4px 8px",
          borderRadius: "4px",
          fontWeight: "bold",
          display: "inline-block",
        };
      default:
        return {
          color: "#6c757d",
          backgroundColor: "#f8f9fa",
          padding: "4px 8px",
          borderRadius: "4px",
          border: "1px solid #dee2e6",
          display: "inline-block",
        };
    }
  };

  // Style for select options with colors
  const getStatusOptionStyle = (status, type = "report") => {
    const statusLower = status?.toLowerCase();

    if (type === "report") {
      switch (statusLower) {
        case "pending":
          return {
            backgroundColor: "#f8d7da",
            color: "#721c24",
            padding: "4px",
          };
        case "assigned":
          return {
            backgroundColor: "#fff3cd",
            color: "#856404",
            padding: "4px",
          };
        case "completed":
          return {
            backgroundColor: "#d4edda",
            color: "#155724",
            padding: "4px",
          };
        case "in progress":
          return {
            backgroundColor: "#d1ecf1",
            color: "#0c5460",
            padding: "4px",
          };
        case "cancelled":
          return {
            backgroundColor: "#e2e3e5",
            color: "#383d41",
            padding: "4px",
          };
        default:
          return {
            backgroundColor: "#f8f9fa",
            color: "#495057",
            padding: "4px",
          };
      }
    } else {
      // Payout status styles
      switch (statusLower) {
        case "paid":
          return {
            backgroundColor: "#d4edda",
            color: "#155724",
            padding: "4px",
          };
        case "processing":
          return {
            backgroundColor: "#fff3cd",
            color: "#856404",
            padding: "4px",
          };
        case "disputed":
          return {
            backgroundColor: "#f8d7da",
            color: "#721c24",
            padding: "4px",
          };
        case "pending":
          return {
            backgroundColor: "#e2e3e5",
            color: "#383d41",
            padding: "4px",
          };
        default:
          return {
            backgroundColor: "#f8f9fa",
            color: "#495057",
            padding: "4px",
          };
      }
    }
  };

  const clearAllFilters = () => {
    setPatientIdFilter("");
    setPatientNameFilter("");
    setInvestigationFilter("");
    setInvestigationCostFilter("");
    setRequestFeeFilter("");
    setRequestPercentageFilter("");
    setReportStatusFilter("");
    setRequestedByFilter("");
    setPayoutStatusFilter("");
    setRequestStartDate("");
    setRequestEndDate("");
    setReportedStartDate("");
    setReportedEndDate("");
  };

  // Check if any filter is active
  const isFilterActive = useMemo(() => {
    return (
      patientIdFilter ||
      patientNameFilter ||
      investigationFilter ||
      investigationCostFilter ||
      requestFeeFilter ||
      requestPercentageFilter ||
      reportStatusFilter ||
      requestedByFilter ||
      payoutStatusFilter ||
      requestStartDate ||
      requestEndDate ||
      reportedStartDate ||
      reportedEndDate
    );
  }, [
    patientIdFilter,
    patientNameFilter,
    investigationFilter,
    investigationCostFilter,
    requestFeeFilter,
    requestPercentageFilter,
    reportStatusFilter,
    requestedByFilter,
    payoutStatusFilter,
    requestStartDate,
    requestEndDate,
    reportedStartDate,
    reportedEndDate,
  ]);

  if (error)
    return <LoaderAnimation loadingTxt="Failed to load CT scan requests." />;
  if (isLoading)
    return <LoaderAnimation loadingTxt="Loading CT scan requests..." />;

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="patient-register">
          <TopActionMenus AddTitle="CT Scan Register" />

          {/* Statistics Cards - Without Averages */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "15px",
              marginBottom: "20px",
            }}
          >
            {/* Total Records Card */}
            <div
              style={{
                backgroundColor: "#6f42c1",
                borderRadius: "8px",
                padding: "15px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                color: "white",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <FontAwesomeIcon
                  icon={faFileInvoice}
                  size="lg"
                  style={{ marginRight: "10px" }}
                />
                <h4 style={{ margin: 0 }}>Total Records</h4>
              </div>
              <div>
                <div style={{ fontSize: "32px", fontWeight: "bold" }}>
                  {statistics.totalRecords}
                </div>
              </div>
            </div>

            {/* Filtered Records Card */}
            <div
              style={{
                backgroundColor: "#fd7e14",
                borderRadius: "8px",
                padding: "15px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                color: "white",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <FontAwesomeIcon
                  icon={faFilter}
                  size="lg"
                  style={{ marginRight: "10px" }}
                />
                <h4 style={{ margin: 0 }}>Filtered Records</h4>
              </div>
              <div>
                <div style={{ fontSize: "32px", fontWeight: "bold" }}>
                  {filteredData.length}
                </div>
              </div>
            </div>

            {/* Investigation Cost Statistics - Total Only */}
            <div
              style={{
                backgroundColor: "#0d6efd",
                borderRadius: "8px",
                padding: "15px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                color: "white",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <FontAwesomeIcon
                  icon={faCoins}
                  size="lg"
                  style={{ marginRight: "10px" }}
                />
                <h4 style={{ margin: 0 }}>Investigation Cost</h4>
              </div>
              <div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  GHC {statistics.totalInvestigationCost.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Request Fee Statistics - Total Only */}
            <div
              style={{
                backgroundColor: "#198754",
                borderRadius: "8px",
                padding: "15px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                color: "white",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <FontAwesomeIcon
                  icon={faCalculator}
                  size="lg"
                  style={{ marginRight: "10px" }}
                />
                <h4 style={{ margin: 0 }}>Request Fee</h4>
              </div>
              <div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                  GHC {statistics.totalRequestFee.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div
            className="filters-section"
            style={{
              backgroundColor: "#f5f5f5",
              padding: "15px",
              marginBottom: "20px",
              borderRadius: "5px",
              border: "1px solid #ddd",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: "15px" }}>Filters</h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: "15px",
              }}
            >
              {/* Text Fields - Patient ID */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Patient ID
                </label>
                <input
                  type="text"
                  placeholder="Filter by Patient ID"
                  value={patientIdFilter}
                  onChange={(e) => setPatientIdFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>

              {/* Text Fields - Patient Full Name */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Patient Full Name
                </label>
                <input
                  type="text"
                  placeholder="Filter by Patient Name"
                  value={patientNameFilter}
                  onChange={(e) => setPatientNameFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>

              {/* Text Fields - Investigation (Now text field) */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Investigation
                </label>
                <input
                  type="text"
                  placeholder="Filter by Investigation"
                  value={investigationFilter}
                  onChange={(e) => setInvestigationFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>

              {/* Text Fields - Investigation Cost */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Investigation Cost (GHC)
                </label>
                <input
                  type="text"
                  placeholder="Filter by Investigation Cost"
                  value={investigationCostFilter}
                  onChange={(e) => setInvestigationCostFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>

              {/* Text Fields - Request Fee */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Request Fee (GHC)
                </label>
                <input
                  type="text"
                  placeholder="Filter by Request Fee"
                  value={requestFeeFilter}
                  onChange={(e) => setRequestFeeFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>

              {/* Text Fields - Request Percentage */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Request Percentage (%)
                </label>
                <input
                  type="text"
                  placeholder="Filter by Percentage"
                  value={requestPercentageFilter}
                  onChange={(e) => setRequestPercentageFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>

              {/* Select Fields - Report Status with Colors */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Report Status
                </label>
                <select
                  value={reportStatusFilter}
                  onChange={(e) => setReportStatusFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">All Statuses</option>
                  {uniqueValues.reportStatuses.map((status) => (
                    <option
                      key={status}
                      value={status}
                      style={getStatusOptionStyle(status, "report")}
                    >
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              {/* Select Fields - Requested By */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Requested By
                </label>
                <select
                  value={requestedByFilter}
                  onChange={(e) => setRequestedByFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">All Requesters</option>
                  {uniqueValues.requesters.map((requester) => (
                    <option key={requester} value={requester}>
                      {requester}
                    </option>
                  ))}
                </select>
              </div>

              {/* Select Fields - Payout Status with Colors */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Payout Status
                </label>
                <select
                  value={payoutStatusFilter}
                  onChange={(e) => setPayoutStatusFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">All Payouts</option>
                  {uniqueValues.payoutStatuses.map((status) => (
                    <option
                      key={status}
                      value={status}
                      style={getStatusOptionStyle(status, "payout")}
                    >
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Fields - Requested Date Range */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Requested Date From
                </label>
                <input
                  type="date"
                  value={requestStartDate}
                  onChange={(e) => setRequestStartDate(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Requested Date To
                </label>
                <input
                  type="date"
                  value={requestEndDate}
                  onChange={(e) => setRequestEndDate(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>

              {/* Date Fields - Reported Date Range */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Reported Date From
                </label>
                <input
                  type="date"
                  value={reportedStartDate}
                  onChange={(e) => setReportedStartDate(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Reported Date To
                </label>
                <input
                  type="date"
                  value={reportedEndDate}
                  onChange={(e) => setReportedEndDate(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
            </div>

            {/* Filter Actions */}
            {isFilterActive && (
              <div
                style={{
                  marginTop: "15px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={clearAllFilters}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ minWidth: "1200px" }}>
              <thead>
                <tr>
                  <th>Patient ID</th>
                  <th>Patient Full Name</th>
                  <th>Investigation</th>
                  <th>Investigation Cost (GHC)</th>
                  <th>Request Fee (GHC)</th>
                  <th>Request Percentage (%)</th>
                  <th>Requested Date</th>
                  <th>Reported Date</th>
                  <th>Report Status</th>
                  <th>Requested By</th>
                  <th>Payout Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((ctrequests) => (
                    <tr key={ctrequests.request_nid}>
                      <td>{ctrequests.field_patient_id}</td>
                      <td>
                        {ctrequests.patient_fullname ===
                        "Patient yet to be attached" ? (
                          <span className="tableyettobeadded">
                            Patient yet to be attached
                          </span>
                        ) : (
                          ctrequests.patient_fullname
                        )}
                      </td>
                      <td>{ctrequests.investigation}</td>
                      <td>{ctrequests.field_investigation_price}</td>
                      <td>{ctrequests.field_requester_payment_commissi}</td>
                      <td>{ctrequests.field_requester_payment_com_perc}</td>
                      <td>{ctrequests.request_date}</td>
                      <td>{ctrequests.report_date}</td>
                      <td>
                        <span
                          style={getReportStatusStyle(
                            ctrequests.field_ct_scan_report_status,
                          )}
                        >
                          {ctrequests.field_ct_scan_report_status || "Not Set"}
                        </span>
                      </td>
                      <td>
                        {ctrequests.requester_first_name}{" "}
                        {ctrequests.requester_last_name}
                      </td>
                      <td>
                        <span
                          style={getPayoutStatusStyle(
                            ctrequests.field_payout_status,
                          )}
                        >
                          {ctrequests.field_payout_status || "Not Set"}
                        </span>
                      </td>
                      <td className="formActionBtn">
                        <FontAwesomeIcon
                          icon={faEye}
                          size="20"
                          color="#9d9c9c"
                          style={{ cursor: "pointer", marginRight: "10px" }}
                          onClick={() => {
                            const reportData = {
                              request_nid: ctrequests.request_nid,
                              request_uuid: ctrequests.request_uuid,
                              patient_fullname: ctrequests.patient_fullname,
                              patient_field_patient_id:
                                ctrequests.field_patient_id,
                              patient_nid: ctrequests.patient_nid,
                              patient_gender: ctrequests.field_gender,
                              patient_date_of_birth:
                                ctrequests.field_date_of_birth,
                              requester_first_name:
                                ctrequests.requester_first_name,
                              requester_last_name:
                                ctrequests.requester_last_name,
                              request_date_created: ctrequests.request_date,
                              report_date_created: ctrequests.report_date,
                              report_nid: ctrequests.report_nid,
                              report_field_ct_scan_report_status:
                                ctrequests.field_ct_scan_report_status,
                              report_report_main_title:
                                ctrequests.report_main_title,
                              report_field_specialty:
                                ctrequests.field_specialty,
                              report_field_ct_scan_report_technique:
                                ctrequests.field_ct_scan_report_technique,
                              report_field_ct_scan_report_findings:
                                ctrequests.field_ct_scan_report_findings,
                              report_field_ct_scan_report_impression:
                                ctrequests.field_ct_scan_report_impression,
                              request_investigation: ctrequests.investigation,
                              request_field_investigation_price:
                                ctrequests.field_investigation_price,
                              request_clinical_diag:
                                ctrequests.field_ct_scan_clinical_diagnosis,
                              request_comments:
                                ctrequests.field_ct_scan_comments,
                              report_uuid: ctrequests.report_uuid,
                              payout_status_key:
                                ctrequests.field_payout_status_key,
                            };
                            setSelectedData(reportData);
                            stateNavigator.navigate("viewctscanreportregister");
                          }}
                        />
                        <FontAwesomeIcon
                          icon={faMoneyBillTransfer}
                          size="20"
                          color="#9d9c9c"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setEditRequestData(ctrequests);
                            toggleModalEditRequest();
                            const reportData = {
                              request_nid: ctrequests.request_nid,
                              request_uuid: ctrequests.request_uuid,
                              patient_fullname: ctrequests.patient_fullname,
                              patient_field_patient_id:
                                ctrequests.field_patient_id,
                              patient_nid: ctrequests.patient_nid,
                              patient_gender: ctrequests.field_gender,
                              patient_date_of_birth:
                                ctrequests.field_date_of_birth,
                              requester_first_name:
                                ctrequests.requester_first_name,
                              requester_last_name:
                                ctrequests.requester_last_name,
                              request_date_created: ctrequests.request_date,
                              report_date_created: ctrequests.report_date,
                              report_nid: ctrequests.report_nid,
                              report_field_ct_scan_report_status:
                                ctrequests.field_ct_scan_report_status,
                              report_report_main_title:
                                ctrequests.report_main_title,
                              report_field_specialty:
                                ctrequests.field_specialty,
                              report_field_ct_scan_report_technique:
                                ctrequests.field_ct_scan_report_technique,
                              report_field_ct_scan_report_findings:
                                ctrequests.field_ct_scan_report_findings,
                              report_field_ct_scan_report_impression:
                                ctrequests.field_ct_scan_report_impression,
                              request_investigation: ctrequests.investigation,
                              request_field_investigation_price:
                                ctrequests.field_investigation_price,
                              request_clinical_diag:
                                ctrequests.field_ct_scan_clinical_diagnosis,
                              request_comments:
                                ctrequests.field_ct_scan_comments,
                              report_uuid: ctrequests.report_uuid,
                              payout_status_key:
                                ctrequests.field_payout_status_key,
                            };
                            setSelectedData(reportData);
                          }}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="12"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No records found matching the selected filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* --- Add CT Request Modal --- */}
          {modalAddRequest && (
            <div className="modal">
              <div className="overlay"></div>
              <div className="modal-content">
                <div className="modal-body">
                  <div className="modal-header">
                    <p>Add new CT - scan request</p>
                    <button
                      className="close-modal"
                      onClick={toggleModalAddRequest}
                    >
                      <FontAwesomeIcon icon={faClose} />
                    </button>
                  </div>
                  <AddCTScanRequestRegister />
                </div>
              </div>
            </div>
          )}

          {/* --- Edit CT Request Modal --- */}
          {modalEditRequest && (
            <div className="modal">
              <div className="overlay"></div>
              <div className="modal-content">
                <div className="modal-body">
                  <div className="modal-header">
                    <p>{`Update Payout for ${editRequestData.patient_fullname} request`}</p>
                    <button
                      className="close-modal"
                      onClick={toggleModalEditRequest}
                    >
                      <FontAwesomeIcon icon={faClose} />
                    </button>
                  </div>
                  <UpdateCTScanRequestPayout
                    requestData={editRequestData}
                    request_nid={ctrequestsData?.[0]?.request_nid}
                    setModalSuccessMsg={setModalSuccessMsg}
                    setIsMessageModalError={setIsMessageModalError}
                  />
                </div>
              </div>
            </div>
          )}

          {/* --- Success Message --- */}
          {modalSuccessMsg && (
            <MessageModal
              leftIcon={faCheckCircle}
              color="green"
              fontSize={20}
              message="Request updated successfully!"
              onClose={() => setModalSuccessMsg(false, toggleModalSuccessMsg())}
              rightIcon={faClose}
            />
          )}

          {/* --- Error Message --- */}
          {isMessageModalError && (
            <MessageModal
              leftIcon={faCancel}
              color="red"
              fontSize={20}
              message="This request is complete. Editing is not allowed."
              onClose={toggleModalError}
              rightIcon={faClose}
            />
          )}

          {/* --- Custom Message --- */}
          {isMessageModalIsNotOk && (
            <MessageModal
              leftIcon={faCancel}
              color="red"
              fontSize={20}
              message={modalMessages}
              onClose={toggleModalIsNotOk}
              rightIcon={faClose}
            />
          )}
        </div>
      </div>
      <DashboardContentFooter />
    </div>
  );
};

export default CtScanReportRegister;
